import { formatTypes } from '../types';
import { Utils } from './loanUtils';

const DEFAULT_MAX_AMOUNT: string = '999999999';

export class NumberFieldWithRange {
    public textInput: HTMLInputElement;

    public rangeInput: HTMLInputElement;

    private container: HTMLElement | null;

    private errorLabel: HTMLElement | null;

    private formatType: formatTypes = 'int';

    private allowDecimals: boolean = false;

    constructor(
        private root: HTMLElement,
        private inputId: string,
        private onUpdate: () => void,
    ) {
        this.textInput = root.querySelector(`#${inputId}`) as HTMLInputElement;
        this.rangeInput = root.querySelector(`#${inputId}Slider`) as HTMLInputElement;
        this.container = this.textInput?.closest('.sgw-currency-input');
        this.errorLabel = root.querySelector(`#${inputId}__error`);
        this.formatType = (this.textInput?.getAttribute('data-mode') ?? 'int') as formatTypes;
        this.allowDecimals = this.formatType === 'percent';

        this.bindEvents();

        (window as any).addEventListener('pageshow', () => {
            this.updateSliderTrack();
        });
    }

    private formatBasedOnType(val: number): string {
        switch (this.formatType) {
            case 'year':
                return Utils.formatAsYear(val);
            case 'currency':
                return Utils.formatCurrency(val);
            case 'percent':
                return Utils.formatPercent(val);
            default:
                return val.toString();
        }
    }

    private bindEvents(): void {
        this.rangeInput.addEventListener('input', () => {
            this.syncTextFromRange();
            this.onUpdate();
        });

        this.textInput.addEventListener('keydown', (e: KeyboardEvent) => {
            if (this.allowDecimals) {
                const { selectionStart, selectionEnd, value } = this.textInput;
                if (e.key === 'Backspace' && selectionStart && selectionStart === selectionEnd) {
                    if (value[selectionStart - 1] === ',') {
                        e.preventDefault();
                    }
                }
                if (e.key === 'Delete' && selectionStart && selectionStart === selectionEnd) {
                    if (value[selectionStart] === ',') {
                        e.preventDefault();
                    }
                }
            }
        });

        this.textInput.addEventListener('input', (e: Event) => {
            const input = this.textInput;
            const oldVal = input.value;
            const selectionStart = input.selectionStart || 0;
            const isDeleting = (e as InputEvent).inputType?.includes('delete');
            const hasTrailingSeparator = /[.,]$/.test(oldVal);

            // mid-decimal edit
            if (this.allowDecimals && (hasTrailingSeparator || (isDeleting && oldVal.includes(',')))) {
                const raw = Utils.parseNumeric(oldVal, true);
                if (!Number.isNaN(raw)) {
                    this.syncRangeFromText();
                    this.onUpdate();
                }
                return;
            }

            const raw = Utils.parseNumeric(oldVal, this.allowDecimals);
            if (raw === 0) {
                return;
            }
            const formatted = this.formatBasedOnType(raw);
            input.value = formatted;
            const suffixMatch = formatted.match(/[^\d]+$/);
            const suffixLength = suffixMatch ? suffixMatch[0].length : 0;
            const maxAllowedPos = formatted.length - suffixLength;

            let newPos = selectionStart;
            if (newPos > maxAllowedPos && formatted.length > 0) {
                newPos = maxAllowedPos;
            }
            input.setSelectionRange(newPos, newPos);

            this.syncRangeFromText();
            this.onUpdate();
        });

        this.textInput.addEventListener('click', () => {
            setTimeout(() => {
                const val = this.textInput.value;
                const suffixMatch = val.match(/[^\d]+$/);

                if (suffixMatch) {
                    const maxPos = val.length - suffixMatch[0].length;
                    const currentPos = this.textInput.selectionStart || 0;

                    if (currentPos > maxPos) {
                        this.textInput.setSelectionRange(maxPos, maxPos);
                    }
                }
            }, 0);
        });

        this.textInput.addEventListener('focus', () => {
            const raw = Utils.parseNumeric(this.textInput.value, this.allowDecimals);
            const formatted = this.formatBasedOnType(raw);
            this.textInput.value = formatted;
            this.onUpdate();
        });

        this.textInput.addEventListener('blur', () => {
            this.safelyUpdate();
        });

        this.syncTextFromRange();
        this.syncRangeFromText();
    }

    public safelyUpdate(): void {
        this.leaveOnlyNumbers();
        this.validate();
        this.syncRangeFromText();
        this.onUpdate();
    }

    private setAriaInvalid(isValid: boolean): void {
        if (isValid) {
            this.textInput.removeAttribute('aria-invalid');
            this.rangeInput.removeAttribute('aria-invalid');
        } else {
            this.textInput.setAttribute('aria-invalid', 'true');
            this.rangeInput.setAttribute('aria-invalid', 'true');
        }
    }

    public validate(): boolean {
        const raw = Utils.parseNumeric(this.textInput.value, this.allowDecimals);
        const min = this.allowDecimals
            ? parseFloat(this.textInput.getAttribute('min') || '0')
            : parseInt(this.textInput.getAttribute('min') || '0', 10);
        const max = this.allowDecimals
            ? parseFloat(this.textInput.getAttribute('max') || DEFAULT_MAX_AMOUNT)
            : parseInt(this.textInput.getAttribute('max') || DEFAULT_MAX_AMOUNT, 10);
        const isValid = raw >= min && raw <= max && raw > 0;

        this.container?.classList.toggle('sgw-currency-input__error', !isValid);

        this.setAriaInvalid(isValid);
        this.setAriaInvalid(isValid);

        if (this.errorLabel) {
            this.errorLabel.style.display = isValid ? 'none' : 'block';

            const errorId = this.errorLabel.id || '';
            if (!isValid && errorId) {
                this.textInput.setAttribute('aria-describedby', errorId);
                this.rangeInput.setAttribute('aria-describedby', errorId);
            } else {
                this.textInput.removeAttribute('aria-describedby');
                this.rangeInput.removeAttribute('aria-describedby');
            }

            if (this.textInput.getAttribute('data-validate') === 'separate') {
                const minError = this.errorLabel.querySelector('[data-validate-separate="min"]')!;
                const maxError = this.errorLabel.querySelector('[data-validate-separate="max"]')!;
                minError.setAttribute('hidden', 'true');
                maxError.setAttribute('hidden', 'true');
                if (raw < min) {
                    minError.removeAttribute('hidden');
                }
                if (raw > max) {
                    maxError.removeAttribute('hidden');
                }
            }
        } else {
            this.textInput.removeAttribute('aria-describedby');
            this.rangeInput.removeAttribute('aria-describedby');
        }

        return isValid;
    }

    public getRawValue(): number {
        return Utils.parseNumeric(this.textInput.value, this.allowDecimals);
    }

    public leaveOnlyNumbers(): void {
        const val = Utils.parseNumeric(this.textInput.value, this.allowDecimals);
        this.textInput.value = this.formatBasedOnType(val);
    }

    private syncTextFromRange(): void {
        const val = this.allowDecimals ? parseFloat(this.rangeInput.value) : parseInt(this.rangeInput.value, 10);
        this.textInput.value = this.formatBasedOnType(val);
        this.updateSliderTrack();
        this.validate();
    }

    private syncRangeFromText(): void {
        const raw = this.getRawValue();
        const maxValue = this.allowDecimals ? parseFloat(this.rangeInput.max) : parseInt(this.rangeInput.max, 10);
        if (raw > maxValue) this.rangeInput.value = this.rangeInput.max;
        this.rangeInput.value = raw.toString();
        this.updateSliderTrack();
    }

    private updateSliderTrack(): void {
        const min = Number(this.rangeInput.min) || 0;
        const max = Number(this.rangeInput.max) || 100;
        const val = Number(this.rangeInput.value) || 0;
        const percent = ((val - min) / (max - min)) * 100;
        this.rangeInput.style.setProperty('--progress', `${percent}%`);
    }
}
